<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import { unavailabilityFormatter } from "@/modules/companyNeed/helpers/columnFormatter";

import type { WorkerPool } from "@/modules/companyNeed/views/details/pool/types";

import Avatar from "@/dsComponents/Avatar/Avatar.vue";
import { AvatarSize } from "@/dsComponents/Avatar/types";
import ButtonAlt from "@/dsComponents/buttons/ButtonAlt.vue";
import QuickActionItem from "@/dsComponents/QuickActions/QuickActionItem.vue";
import TableCell from "@/dsComponents/Table/TableCell.vue";
import { type TableCellProps, TableFontColor } from "@/dsComponents/Table/types";

const props = defineProps<{
    worker: WorkerPool;
    isAdded: boolean;
    isPending: boolean;
    agencyLabel?: string | null;
}>();

const emit = defineEmits<{
    add: [];
}>();

const { t } = useI18n();

const statusLine = computed((): TableCellProps | null => {
    if (props.agencyLabel) {
        return {
            cellTitle: props.agencyLabel,
            fontType: "passive",
        };
    }

    if (!props.worker.delegable) {
        return {
            cellTitle: t("page.companyNeed.details.table.pool.nonDelegable"),
            fontType: "passive",
            fontColor: TableFontColor.red,
            icon: {
                iconType: "wrong-way",
                iconColor: "red",
            },
        };
    }
    if (props.worker.unavailability) {
        return unavailabilityFormatter(props.worker.unavailability);
    }
    return null;
});

const addButtonLabel = computed(() =>
    props.isAdded
        ? t("modals.missionDetails.propositions.addFromPool.added")
        : t("global.actions.add"),
);

const addButtonClass = computed(() =>
    props.isAdded ? "pool-workers-qa-item__added" : "pool-workers-qa-item__cta",
);
</script>
<template>
    <div class="pool-workers-qa-item">
        <QuickActionItem :selected="false">
            <template #infos>
                <div class="pool-workers-qa-item__infos">
                    <Avatar
                        :firstname="worker.firstname"
                        :lastname="worker.lastname"
                        :image-url="worker.avatarUrl"
                        :size="AvatarSize.size16"
                    />
                    <div class="pool-workers-qa-item__name typo-xs-medium">{{ worker.firstname }} {{ worker.lastname }}</div>
                </div>
            </template>
            <template #more-infos>
                <TableCell v-if="statusLine" v-bind="statusLine" />
            </template>
            <template #second-details>
                <div class="pool-workers-qa-item__action" @click.stop>
                    <ButtonAlt
                        :class="addButtonClass"
                        type="neutral"
                        :variant="isAdded ? 'ghost' : 'stroke'"
                        size="sm"
                        :shadow="!isAdded"
                        :loading="isPending"
                        :disabled="isPending || isAdded"
                        :left-icon="isAdded ? 'check' : undefined"
                        :label="addButtonLabel"
                        @click="emit('add')"
                    />
                </div>
            </template>
        </QuickActionItem>
    </div>
</template>
<style scoped lang="scss">
.pool-workers-qa-item {
    .search-result-item__more-infos {
        width: 232px;
        flex: 0 0 232px;
    }

    .search-result-item__details:first-of-type {
        width: max-content;
        min-width: 0;
        flex: 0 0 auto;
        justify-content: flex-end;
        box-sizing: border-box;
    }

    .search-result-item__details:last-of-type {
        display: none;
    }
}

.pool-workers-qa-item__infos {
    display: flex;
    min-width: 0;
    align-items: center;
    gap: var(--spacing-lg, 12px);
}

.pool-workers-qa-item__name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: var(--text-sub-600, #494e61);
}

.pool-workers-qa-item__action {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    width: max-content;
    min-width: 0;
    box-sizing: border-box;
}

.pool-workers-qa-item__added,
.pool-workers-qa-item__cta {
    color: var(--text-sub-600, #494e61);
}

.pool-workers-qa-item__cta {
    border-radius: var(--radius-sm, 6px);
}
</style>
